import { copyIcon, filterIcon } from '@progress/kendo-svg-icons';
import { ACTION_GROUPS, ActionItemId, ClickedCellMetadata, DropDownButtonItem } from './FilterActions.types';
import { isTransferableMetadataField } from './buildSimilarDocumentMetadata';
import { TranslateFn } from './filterActionsHelpers';

interface BuildContextMenuItemsParams {
  items: DropDownButtonItem[];
  translate: TranslateFn;
  clickedCell: ClickedCellMetadata | undefined;
  ctsWithCreatePermissions: string[] | undefined;
  canFilterByCell: boolean;
}

function buildCreateFromCellMenuItem(
  translate: TranslateFn,
  clickedCell: ClickedCellMetadata | undefined,
  ctsWithCreatePermissions: string[] | undefined
): DropDownButtonItem | undefined {
  if (!clickedCell || !isTransferableMetadataField(clickedCell.fieldName)) {
    return undefined;
  }

  if (!ctsWithCreatePermissions?.includes(clickedCell.contentType)) {
    return undefined;
  }

  return {
    id: ActionItemId.CREATE_FROM_CELL,
    text: translate('custom.toolbar.actions.form.createFromCellTitle'),
    svgIcon: copyIcon,
    groupId: ACTION_GROUPS.DOCUMENT_GROUP
  };
}

function buildFilterByCellMenuItem(
  translate: TranslateFn,
  clickedCell: ClickedCellMetadata | undefined,
  canFilterByCell: boolean
): DropDownButtonItem | undefined {
  if (!clickedCell || !canFilterByCell) {
    return undefined;
  }

  return {
    id: ActionItemId.FILTER_BY_CELL,
    text: translate('custom.toolbar.actions.form.filterByCellTitle'),
    svgIcon: filterIcon,
    groupId: ACTION_GROUPS.DOCUMENT_GROUP
  };
}

function insertCellItems(items: DropDownButtonItem[], cellItems: DropDownButtonItem[]): DropDownButtonItem[] {
  const similarIndex = items.findIndex(item => item.id === ActionItemId.CREATE_SIMILAR);
  if (similarIndex >= 0) {
    return [...items.slice(0, similarIndex + 1), ...cellItems, ...items.slice(similarIndex + 1)];
  }

  const addNewIndex = items.findIndex(item => item.id === ActionItemId.ADD_NEW);
  if (addNewIndex >= 0) {
    return [...items.slice(0, addNewIndex + 1), ...cellItems, ...items.slice(addNewIndex + 1)];
  }

  return [...items, ...cellItems];
}

export function buildContextMenuItems(params: BuildContextMenuItemsParams): DropDownButtonItem[] {
  const rowItems = params.items.filter(item => item.groupId !== ACTION_GROUPS.GRID_GROUP);
  const cellItems = [
    buildCreateFromCellMenuItem(params.translate, params.clickedCell, params.ctsWithCreatePermissions),
    buildFilterByCellMenuItem(params.translate, params.clickedCell, params.canFilterByCell)
  ].filter((item): item is DropDownButtonItem => item !== undefined);

  if (cellItems.length === 0) {
    return rowItems;
  }

  return insertCellItems(rowItems, cellItems);
}
